import { message } from '@/components/Message';
import {
  Card,
  CardType,
  CardUser,
  CardUserActionType,
  CardUserStatus,
  getCardTypeDescTag,
  getCardUserStatusDescTag,
  requestActiveCardUser,
  requestCardUserLog,
} from '@/models/card';
import { DateFormat } from '@/models/common';
import { requestUserBasicInfo } from '@/models/user';
import {
  ClockCircleFilled,
  ExclamationCircleOutlined,
  FieldTimeOutlined,
  InfoCircleFilled,
  MinusOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Flex, List, Space, Tag, Typography } from 'antd';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { CardSkuInfoPopover } from '.';
import Modal from '../Modal';
import PosButton from '../PosButton';
import UserAvatar from '../User/UserAvatar';
import { CardRenewForm } from './cardRenewForm';
import styles from './cardUser.less';

type Props = {
  card: CardUser;
  onClick?: (card: CardUser) => void;
  showAddTime?: boolean;
  showUserInfo?: boolean;
  selectMode?: boolean;
  /**是否可选择, 可选择: true , 不可选择: 不可选择原因 */
  selectableInfo?: {
    max: number;
    available: boolean;
    /**不可用原因 */
    NAReason?: string;
  };
  onSelect?: (count: number) => void;
  selectCount?: number;
};

const CardUserView: React.FC<Props> = ({
  card: initCard,
  showAddTime = true,
  showUserInfo = false,
  selectMode = false,
  onClick = () => {},
  selectableInfo = { max: 0, available: false },
  onSelect = () => undefined,
  selectCount,
}: Props) => {
  const { xs } = useBreakpoint();
  const [card, setCard] = useState(initCard);
  const [detailMode, setDetailMode] = useState<'log' | 'renew'>(null);
  const { send: getUserBasicInfo, payload: userInfo } = requestUserBasicInfo();
  const { send: activeCardUser } = requestActiveCardUser();
  const {
    send: sendGetCardUserLog,
    payload: cardUserLogPayload,
    loading: cardUserLogLoading,
    error: cardUserLogError,
  } = requestCardUserLog();

  useEffect(() => {
    setCard(initCard);
  }, [initCard]);

  useEffect(() => {
    if (showUserInfo && card && card.userId)
      getUserBasicInfo({
        pathVars: { userId: card.userId },
      }).then();
  }, [showUserInfo, card]);

  const getCardUserLog = (page = 1) => {
    sendGetCardUserLog({
      params: {
        cardUserId: card.id,
        page,
        limit: 3,
        actionType: CardUserActionType.CONSUME,
      },
    }).then();
  };

  const logPage = cardUserLogPayload?.page;

  return (
    <div className={`${styles.wrapper} ${xs ? styles.xs : ''}`}>
      <div
        className={styles.container}
        onClick={() => {
          onClick(card);
        }}>
        <div className={styles.main}>
          <div className={styles.heading}>
            {selectMode ? null : getCardUserStatusDescTag(card)}{' '}
            <span>
              {card.cardType === CardType.TIMES && card.times !== undefined ? (
                <>
                  <Tag icon={<InfoCircleFilled />} color={'#2db7f5'} style={{ margin: 0 }}>
                    剩{card.times}次
                  </Tag>{' '}
                </>
              ) : null}
              {card.expiredTime !== undefined ? (
                <>
                  <Tag icon={<ClockCircleFilled />} color={'#2db7f5'} style={{ margin: 0 }}>
                    {dayjs(card.expiredTime).format(DateFormat)}
                  </Tag>
                </>
              ) : null}
            </span>
          </div>

          <CardSkuInfoPopover card={card}>
            <Flex className={styles.name} align="center" wrap>
              {getCardTypeDescTag({ type: card.cardType, durationUnit: card.cardDurationUnit } as Card)}
              <div className={styles.nameText}>
                <Typography.Title level={5} ellipsis={{ rows: 1 }}>
                  {card.cardName}
                </Typography.Title>
              </div>
            </Flex>
          </CardSkuInfoPopover>

          {selectableInfo && selectableInfo.NAReason ? (
            <Tag icon={<ExclamationCircleOutlined />} color="warning">
              {selectableInfo.NAReason}
            </Tag>
          ) : null}
          {!selectMode && (
            <Space direction="vertical" size={'small'}>
              <Typography.Paragraph type="secondary" className={styles.intro} ellipsis={{ rows: 2 }}>
                {card.cardIntro}
              </Typography.Paragraph>
              <Space size={'small'} direction={xs ? 'vertical' : 'horizontal'}>
                {showAddTime && (
                  <Typography.Text type={'secondary'}>
                    <FieldTimeOutlined /> 购卡时间: {card.addTime}
                  </Typography.Text>
                )}
              </Space>
            </Space>
          )}
        </div>
        <div className={styles.operations}>
          <Space direction={xs ? 'horizontal' : 'vertical'}>
            {selectMode && card.status === CardUserStatus.CARD_USER_STATUS_ACTIVE ? (
              // 选择模式下(pos结算) 只有可用状态的卡可操作
              <Space>
                <PosButton
                  disabled={!selectableInfo.available || selectCount === 0}
                  icon={<MinusOutlined />}
                  onClick={() => {
                    onSelect(Math.max(0, selectCount - 1));
                  }}
                />
                <Typography.Text className="font-monospace">{selectCount}</Typography.Text>
                <PosButton
                  disabled={!selectableInfo.available || selectCount === selectableInfo.max}
                  icon={<PlusOutlined />}
                  onClick={() => {
                    onSelect(Math.min(selectCount + 1, selectableInfo.max));
                  }}
                />
              </Space>
            ) : null}
            {!selectMode && (
              <>
                <PosButton
                  onClick={() => {
                    if (detailMode === 'log') {
                      setDetailMode(null);
                    } else {
                      setDetailMode('log');
                      getCardUserLog(1);
                    }
                  }}>
                  {detailMode === 'log' ? <>收起记录</> : <>使用记录</>}
                </PosButton>

                {/* 只有激活和过期的卡可以续期 */}
                {(card.status === CardUserStatus.CARD_USER_STATUS_ACTIVE ||
                  card.status === CardUserStatus.CARD_USER_STATUS_EXPIRED) && (
                  <PosButton
                    onClick={() => {
                      if (detailMode === 'renew') {
                        setDetailMode(null);
                      } else {
                        setDetailMode('renew');
                      }
                    }}>
                    卡片续期
                  </PosButton>
                )}

                <CardSkuInfoPopover card={card}>
                  <PosButton onClick={() => {}}>适用范围</PosButton>
                </CardSkuInfoPopover>
              </>
            )}
          </Space>
          {card.status === CardUserStatus.CARD_USER_STATUS_INACTIVE && (
            <>
              <PosButton
                type={'primary'}
                onClick={() => {
                  Modal.confirm({
                    title: '确认激活该卡?',
                    onOk: () =>
                      new Promise<void>((resolve, reject) => {
                        activeCardUser({
                          data: card,
                        })
                          .then(newCard => {
                            message.success(`激活成功`);
                            setCard(Object.assign({}, card, newCard));
                            resolve();
                          })
                          .catch(e => {
                            message.error(`激活失败: ${e.msg}`);
                            reject();
                          });
                      }),
                  });
                }}>
                激活
              </PosButton>
            </>
          )}
        </div>
        {userInfo && (
          <div className={styles.user}>
            <UserAvatar user={userInfo} avatarProps={{ size: 24 }} />
          </div>
        )}
      </div>
      {detailMode === 'log' && (
        <div className={styles.log}>
          <List
            loading={cardUserLogLoading}
            itemLayout="horizontal"
            size={'small'}
            loadMore={
              <div className={'align-flex-horizontal-center'} style={{ paddingTop: 10 }}>
                <PosButton
                  disabled={logPage && logPage.current === 1}
                  loading={cardUserLogLoading}
                  style={{ marginRight: 10 }}
                  onClick={() => {
                    getCardUserLog(logPage?.current - 1);
                  }}>
                  上一页
                </PosButton>
                <PosButton
                  disabled={logPage && logPage.current >= logPage.pages}
                  loading={cardUserLogLoading}
                  style={{ marginLeft: 10 }}
                  onClick={() => {
                    getCardUserLog(logPage?.current + 1);
                  }}>
                  下一页
                </PosButton>
              </div>
            }
            dataSource={cardUserLogPayload?.list}
            renderItem={log => (
              <List.Item>
                <Space size={20}>
                  <Typography.Text>{log.addTime}</Typography.Text>
                  <Typography.Text type={'secondary'} style={{ fontWeight: 'normal' }}>
                    使用<Typography.Text>{log.times}</Typography.Text>次
                  </Typography.Text>
                </Space>
              </List.Item>
            )}
          />
        </div>
      )}
      {detailMode === 'renew' && (
        <div className={styles.renew}>
          <CardRenewForm
            cardUser={card}
            onRenew={newCard => {
              message.success('续期成功，到期时间已更新');
              setCard(Object.assign({}, card, newCard));
              setDetailMode(null);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CardUserView;
